import { SxProps, TextField, Theme } from '@mui/material'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { observer } from 'mobx-react'
import moment from 'moment'
import React, { useState } from 'react'
import {
  Control,
  FieldValues,
  Path,
  PathValue,
  useController,
} from 'react-hook-form'
import { getImageSrc } from '../../../helpers/getImageSrc'

interface CustomDatePickerProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  rules?: any
  defaultValue?: PathValue<T, Path<T>>
  shouldUnregister?: boolean
  sx?: SxProps<Theme>
  disabled?: boolean
  required?: boolean
  minDate?: any
  maxDate?: any
  customError?: string | boolean
}

const FilterDatePicker = <T extends FieldValues>({
  name,
  control,
  label,
  rules,
  defaultValue,
  shouldUnregister,
  maxDate,
  disabled,
  minDate,
  customError,
}: CustomDatePickerProps<T>) => {
  const [dateError, setDateError] = useState(false)
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  })

  const shouldIncludeAllDay = name.toLowerCase().includes('to')
  const getNewValue = (newValue: string) => {
    if (shouldIncludeAllDay) {
      return moment(newValue).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
    } else return moment(newValue).format('YYYY-MM-DDTHH:mm:ssZ')
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DesktopDatePicker
        leftArrowButtonText={'Предыдущий месяц'}
        rightArrowButtonText={'Следующий месяц'}
        minDate={minDate && minDate}
        maxDate={maxDate && maxDate}
        disabled={disabled}
        renderInput={({ inputProps, ...props }) => {
          return (
            // @ts-ignore
            <TextField
              placeholder={'ДД.MM.ГГГГ'}
              fullWidth={true}
              onBlur={() => {
                if (dateError) {
                  onChange(defaultValue ?? null)
                  setDateError(false)
                }
              }}
              onKeyDown={
                ((event: any) => {
                  if (event.key === 'Enter' && dateError) {
                    event.preventDefault()
                    event.stopPropagation()
                  }
                }) as any
              }
              {...props}
              {...inputProps}
            />
          )
        }}
        onError={(e) => {
          setDateError(!!e)
        }}
        value={value}
        onChange={(newValue) => {
          if (moment(newValue, 'DD.MM.YYYY', true).isValid()) {
            onChange(getNewValue(newValue))
          } else if (newValue === null) {
            onChange(null)
          } else {
            onChange(newValue)
          }
        }}
        InputProps={{ error: !!error || !!customError }}
        label={label}
        inputFormat={'DD.MM.YYYY'}
        components={{
          OpenPickerIcon: () => (
            <img alt={'calendar icon'} src={getImageSrc('date-picker')} />
          ),
        }}
        ref={ref}
      />
    </LocalizationProvider>
  )
}

export default observer(FilterDatePicker)
